import React, { FC, useState, useEffect, useMemo } from 'react';
import { useGetProgramMasterData } from 'src/api/mutations/useGetProgramMasterData';
import './SelectEquipmentStep.scss';

import absCrunchMachineImg from 'url:../../../../../../../assets/images/onboarding-trainer/equipment/abs_crunch_machine.jpg';
import barbellImg from 'url:../../../../../../../assets/images/onboarding-trainer/equipment/barbell.jpg';
import benchImg from 'url:../../../../../../../assets/images/onboarding-trainer/equipment/bench.jpg';
import chestPressImg from 'url:../../../../../../../assets/images/onboarding-trainer/equipment/chest_press.jpg';
import crunchBenchImg from 'url:../../../../../../../assets/images/onboarding-trainer/equipment/crunch_bench.jpg';
import dumbellImg from 'url:../../../../../../../assets/images/onboarding-trainer/equipment/dumbell.jpg';
import fitnessBallImg from 'url:../../../../../../../assets/images/onboarding-trainer/equipment/fitness_ball.jpg';
import foamRollerImg from 'url:../../../../../../../assets/images/onboarding-trainer/equipment/foam_roller.jpg';
import hipAdbuctionImg from 'url:../../../../../../../assets/images/onboarding-trainer/equipment/hip_adbuction.jpg';
import hipAdbuction2Img from 'url:../../../../../../../assets/images/onboarding-trainer/equipment/hip_adbuction_2.jpg';
import jumpRopeImg from 'url:../../../../../../../assets/images/onboarding-trainer/equipment/jump_rope.jpg';
import kettlebellImg from 'url:../../../../../../../assets/images/onboarding-trainer/equipment/kettlebell.jpg';
import latPullDownImg from 'url:../../../../../../../assets/images/onboarding-trainer/equipment/lat_pull_down.jpg';
import legCurlImg from 'url:../../../../../../../assets/images/onboarding-trainer/equipment/leg_curl.jpg';
import legExtensionImg from 'url:../../../../../../../assets/images/onboarding-trainer/equipment/leg_extension.jpg';
import legPressImg from 'url:../../../../../../../assets/images/onboarding-trainer/equipment/leg_press.jpg';
import newCrossoverImg from 'url:../../../../../../../assets/images/onboarding-trainer/equipment/new_crossover.jpg';
import pulleyMachineImg from 'url:../../../../../../../assets/images/onboarding-trainer/equipment/pulley_machine.jpg';
import seatedRowMachineImg from 'url:../../../../../../../assets/images/onboarding-trainer/equipment/seated_row_machine.jpg';
import shoulderPressImg from 'url:../../../../../../../assets/images/onboarding-trainer/equipment/shoulder_press.jpg';
import tricepsDipMachineImg from 'url:../../../../../../../assets/images/onboarding-trainer/equipment/triceps_dip_machine.jpg';
import yogaMatImg from 'url:../../../../../../../assets/images/onboarding-trainer/equipment/yoga_mat.jpg';
import { ReactComponent as RadioTickIcon } from '../../../../../../../assets/images/onboarding-trainer/dashboard/program-generator-ai/components/radio-tick-2.svg';

export const equipmentImageMap: Record<string, string> = {
  dumbell: dumbellImg,
  kettlebell: kettlebellImg,
  barbell: barbellImg,
  bench: benchImg,
  crunch_bench: crunchBenchImg,
  abs_crunch_machine: absCrunchMachineImg,
  chest_press: chestPressImg,
  fitness_ball: fitnessBallImg,
  foam_roller: foamRollerImg,
  hip_adbuction: hipAdbuctionImg,
  hip_adbuction_2: hipAdbuction2Img,
  jump_rope: jumpRopeImg,
  lat_pull_down: latPullDownImg,
  leg_curl: legCurlImg,
  leg_extension: legExtensionImg,
  leg_press: legPressImg,
  new_crossover: newCrossoverImg,
  pulley_machine: pulleyMachineImg,
  seated_row_machine: seatedRowMachineImg,
  shoulder_press: shoulderPressImg,
  triceps_dip_machine: tricepsDipMachineImg,
  yoga_mat: yogaMatImg,
};

interface SelectEquipmentStepProps {
  value: string[];
  onChange: (val: string[]) => void;
  isMandatory?: boolean;
}

// Select equipment
// Select all the equipment available
export const SelectEquipmentStep: FC<SelectEquipmentStepProps> = ({
  value,
  onChange,
  isMandatory,
}) => {
  const { data: programMasterData, isLoading, isError } = useGetProgramMasterData();
  const stepData = programMasterData?.gymEquipments;

  const tabs = useMemo(() => stepData?.tabs || [], [stepData?.tabs]);
  const equipmentOptions = useMemo(() => stepData?.options || [], [stepData?.options]);
  const [tab, setTab] = useState<string>('');

  useEffect(() => {
    if (tabs.length > 0 && !tab) {
      setTab(tabs[0].id);
    }
  }, [tabs, tab]);

  const sortedOptions = [...equipmentOptions].sort((a, b) => (a.order || 0) - (b.order || 0));
  const filteredEquipment = sortedOptions.filter((item) => item.tab === tab);

  const toggleEquipment = (id: string) => {
    if (value.includes(id)) {
      onChange(value.filter((itemId) => itemId !== id));
    } else {
      onChange([...value, id]);
    }
  };

  if (isLoading) {
    return <div className="select-equipment-step">{'Loading options...'}</div>;
  }

  if (isError || !stepData) {
    return <div className="select-equipment-step">{'Error loading options.'}</div>;
  }

  return (
    <div className="select-equipment-step">
      <h2 className="step-title">{stepData?.title}</h2>
      <p className="step-subtitle">
        {isMandatory ? 'Select at least one item to continue' : stepData?.subtitle}
      </p>

      <div className="tab-toggle">
        {tabs.map((t) => (
          <button
            key={t.id}
            className={`tab ${tab === t.id ? 'active' : ''}`}
            onClick={() => setTab(t.id)}
          >
            {t.title}
          </button>
        ))}
      </div>

      <div className="equipment-list-wrapper">
        <div className={`equipment-list ${filteredEquipment.length > 3 ? 'is-scrollable' : ''}`}>
          {filteredEquipment.map((item) => (
            <div
              className={`equipment-item ${value.includes(item.slug) ? 'selected' : ''}`}
              key={item.id}
              onClick={() => toggleEquipment(item.slug)}
            >
              <div className="equipment-info">
                <div className="equipment-avatar">
                  <img
                    src={equipmentImageMap[item.image]}
                    alt={item.title}
                    className="avatar-img"
                  />
                </div>
                <span className="equipment-name">{item.title}</span>
              </div>
              <div className="equipment-radio">
                {value.includes(item.slug) ? (
                  <RadioTickIcon />
                ) : (
                  <div className="radio-circle"></div>
                )}
              </div>
            </div>
          ))}
        </div>
        {filteredEquipment.length > 4 && <div className="list-fade-overlay"></div>}
      </div>
    </div>
  );
};
